import React, { useCallback, useState } from 'react';
import { ElementsConsumer } from '@stripe/react-stripe-js';
import { withRouter } from 'react-router-dom';
import './Payment.css';
import { message } from 'antd';

import CustomField from './Components/CustomField';
import CardField from './Components/CardField';
import * as payments from '../../services/providers/payments';

const SubmitButton = ({ processing, error, children, disabled }) => (
  <button
    className={`SubmitButton ${error ? 'SubmitButton--error' : ''}`}
    type="submit"
    disabled={processing || disabled}
  >
    {processing ? 'Processing...' : children}
  </button>
);

const ErrorMessage = ({ children }) => (
  <div className="ErrorMessage" role="alert">
    <svg width="16" height="16" viewBox="0 0 17 17">
      <path
        fill="#FFF"
        d="M8.5,17 C3.80557963,17 0,13.1944204 0,8.5 C0,3.80557963 3.80557963,0 8.5,0 C13.1944204,0 17,3.80557963 17,8.5 C17,13.1944204 13.1944204,17 8.5,17 Z"
      />
      <path
        fill="#6772e5"
        d="M8.5,7.29791847 L6.12604076,4.92395924 C5.79409512,4.59201359 5.25590488,4.59201359 4.92395924,4.92395924 C4.59201359,5.25590488 4.59201359,5.79409512 4.92395924,6.12604076 L7.29791847,8.5 L4.92395924,10.8739592 C4.59201359,11.2059049 4.59201359,11.7440951 4.92395924,12.0760408 C5.25590488,12.4079864 5.79409512,12.4079864 6.12604076,12.0760408 L8.5,9.70208153 L10.8739592,12.0760408 C11.2059049,12.4079864 11.7440951,12.4079864 12.0760408,12.0760408 C12.4079864,11.7440951 12.4079864,11.2059049 12.0760408,10.8739592 L9.70208153,8.5 L12.0760408,6.12604076 C12.4079864,5.79409512 12.4079864,5.25590488 12.0760408,4.92395924 C11.7440951,4.59201359 11.2059049,4.59201359 10.8739592,4.92395924 L8.5,7.29791847 L8.5,7.29791847 Z"
      />
    </svg>
    {children}
  </div>
);

const ResetButton = ({ onClick }) => (
  <button type="button" className="ResetButton" onClick={onClick}>
    <svg width="32px" height="32px" viewBox="0 0 32 32">
      <path
        fill="#FFF"
        d="M15,7.05492878 C10.5000495,7.55237307 7,11.3674463 7,16 C7,20.9705627 11.0294373,25 16,25 C20.9705627,25 25,20.9705627 25,16 C25,15.3627484 24.4834055,14.8461538 23.8461538,14.8461538 C23.2089022,14.8461538 22.6923077,15.3627484 22.6923077,16 C22.6923077,19.6960595 19.6960595,22.6923077 16,22.6923077 C12.3039405,22.6923077 9.30769231,19.6960595 9.30769231,16 C9.30769231,12.3039405 12.3039405,9.30769231 16,9.30769231 L16,12.0841673 C16,12.1800431 16.0275652,12.2738974 16.0794108,12.354546 C16.2287368,12.5868311 16.5380938,12.6540826 16.7703788,12.5047565 L22.3457501,8.92058924 L22.3457501,8.92058924 C22.4060014,8.88185624 22.4572275,8.83063012 22.4959605,8.7703788 C22.6452866,8.53809377 22.5780351,8.22873685 22.3457501,8.07941076 L22.3457501,8.07941076 L16.7703788,4.49524351 C16.6897301,4.44339794 16.5958758,4.41583275 16.5,4.41583275 C16.2238576,4.41583275 16,4.63969037 16,4.91583275 L16,7 L15,7 L15,7.05492878 Z M16,32 C7.163444,32 0,24.836556 0,16 C0,7.163444 7.163444,0 16,0 C24.836556,0 32,7.163444 32,16 C32,24.836556 24.836556,32 16,32 Z"
      />
    </svg>
  </button>
);

function CheckoutForm({ id, clientSecret, location, history, stripe, elements }) {
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  const onChangeEmail = useCallback((e) => setEmail(e.target.value), [setEmail]);
  const onChangeCardField = useCallback((e) => {
    setError(e.error);
    console.log(e);

    setCardComplete(e.complete);
  }, [setError, setCardComplete]);

  const onSubmit = useCallback(async (event) => {
      event.preventDefault();

      if (!stripe || !elements) {
        // Stripe.js has not loaded yet. Make sure to disable
        // form submission until Stripe.js has loaded.
        return;
      }

      if (error) {
        elements.getElement('card').focus();
        return;
      }

      if (cardComplete) {
        setProcessing(true);
      }

      const { name, address, store_name, phone_number } = location.state;

      await payments.update(id, { name, address, store_name, phone_number, email });

      const payment = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement('card'),
          billing_details: {
            name,
            email,
            phone: phone_number,
          },
        },
      });

      if (payment.error) {
        setProcessing(false);
        return message.error(`Something went wrong. ${payment.error.message}`, 10);
      }

      history.push('/download');

      setProcessing(false);
      setSuccess(true);
    },
    [clientSecret, processing, stripe, elements, error, id, location, setProcessing, cardComplete]);

  return (
    <form className="Form" onSubmit={onSubmit}>
      <fieldset className="FormGroup">
        <CustomField
          label="Email"
          id="email"
          type="email"
          placeholder="janedoe@gmail.com"
          required
          autoComplete="email"
          value={email}
          onChange={onChangeEmail}
        />
      </fieldset>
      <fieldset className="FormGroup">
        <CardField onChange={onChangeCardField} />
      </fieldset>
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      <SubmitButton processing={processing} error={error} disabled={!stripe}>
        Pay $99.99
      </SubmitButton>
    </form>
  );
}

export default withRouter(withStripeElements(CheckoutForm));

function withStripeElements(Element) {
  return function (props) {
    return (
      <ElementsConsumer>
        {({ stripe, elements }) => (
          <Element stripe={stripe} elements={elements} {...props} />
        )}
      </ElementsConsumer>
    );
  };
}



// WEBPACK FOOTER //
// ./src/containers/Payment/Form.js